import { IAMessage } from 'core/components/conversation/IAMessage'
import { Documents } from 'core/components/documents/Documents'
import { IApplicationPromptVariable } from 'core/interfaces/Application'
import { IMessage, UserType } from 'core/interfaces/Conversation'
import { IDocument } from 'core/interfaces/Document'
import { RootState } from 'core/store'
import { ConversationHeader } from 'iastart/components/ConversationHeader'
import { VariablesInput } from 'iastart/components/VariablesInput'
import { getMessages } from 'iastart/store/slices/conversationSlice'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

function safeJsonParse(value: string): unknown | null {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function asStringArray(value: unknown): string[] | null {
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return value
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value]
  }
  return null
}

export const PromptTest: React.FC<{
  application: IApplicationPromptVariable
}> = ({ application }) => {
  const { t } = useTranslation()
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const messages: IMessage[] = useSelector((state: RootState) =>
    getMessages(state, application.nomTechnique)
  )
  const [documents, setDocuments] = useState<IDocument[]>([])
  const [isSending, setIsSending] = useState<boolean>(false)
  const formats: string[] | null = useMemo(() => {
    if (!application.format) return null
    const parsed = safeJsonParse(application.format)
    if (parsed && typeof parsed === 'object' && 'outputFile' in parsed) {
      return asStringArray((parsed as { outputFile?: unknown }).outputFile)
    }
    return null
  }, [application.format])

  return (
    <div className="flex items-center w-2/3 mx-auto h-full">
      <div className="conversation">
        <div
          ref={messagesContainerRef}
          className="w-full flex-grow overflow-auto"
        >
          <ConversationHeader application={application} />
          <div className="messages-wrapper">
            <IAMessage
              message={{
                content:
                  application.accroche !== ''
                    ? application.accroche
                    : t('iastart.application.accroche', {
                        application: application.nomApplication,
                      }),
                type: UserType.ASSISTANT,
                timestamp: Date.now(),
              }}
              application={application.nomApplication}
              first
            />
            {isSending && (
              <IAMessage
                message={{
                  content: '',
                  type: UserType.ASSISTANT,
                  timestamp: Date.now(),
                }}
                application={application.nomApplication}
                isLoading
              />
            )}
            {!isSending &&
              messages.map((message, index) => {
                return (
                  <IAMessage
                    key={index}
                    message={message}
                    application={application.nomTechnique}
                    formats={formats}
                  />
                )
              })}
          </div>
        </div>
        <div className="message-input-wrapper">
          <div className="flex flex-col my-[10px]">
            <Documents documents={documents} setDocuments={setDocuments} />
            <VariablesInput
              application={application}
              setIsSending={setIsSending}
              documents={documents}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
